import { AddState, EmptyState, Loader } from '@orbitalwatch/design';
import type { ReactNode } from 'react';

export interface LoadingStateProps {
    /** Caption and accessible name. Default "Loading…". */
    text?: string;
    /** `inline` 24 px spinner in the flow; `block` (default) the 48 px rings centred in a padded box. */
    variant?: 'inline' | 'block';
    className?: string;
}

/** Loading placeholder: library `Loader` in a self-contained box (`ds.css` "states"). */
export function LoadingState(props: LoadingStateProps) {
    const inline = props.variant === 'inline';
    return (
        <div className={['ark-state', inline ? 'is-inline' : '', props.className].filter(Boolean).join(' ')}>
            <Loader size={inline ? 'sm' : 'md'} text={props.text ?? 'Loading…'} />
        </div>
    );
}

export interface MessageStateProps {
    title: ReactNode;
    text?: ReactNode;
    /** Primary action. */
    actionLabel?: string;
    onAction?: () => void;
    actionIcon?: ReactNode;
    /** Link-style secondary action. */
    secondaryLabel?: string;
    onSecondary?: () => void;
    secondaryHref?: string;
    /** `add`: the planet with a plus (the empty area is where to create the first item). Default `empty`. */
    kind?: 'empty' | 'add';
    /** Smaller drawing (sub-nav, table cells). */
    compact?: boolean;
    /** Dashed outline. */
    bordered?: boolean;
    /** Replaces the orbit drawing; `null` hides it. */
    illustration?: ReactNode | null;
    className?: string;
}

/** Empty / no-results / error message block: library `EmptyState` (or `AddState`) with Ark's copy. */
export function MessageState(props: MessageStateProps) {
    const State = props.kind === 'add' ? AddState : EmptyState;
    return (
        <State
            className={props.className}
            title={props.title}
            text={props.text ?? null}
            buttonLabel={props.actionLabel}
            onClick={props.onAction}
            buttonIcon={props.actionIcon}
            secondaryLabel={props.secondaryLabel}
            onSecondaryClick={props.onSecondary}
            secondaryHref={props.secondaryHref}
            compact={props.compact}
            bordered={props.bordered}
            illustration={props.illustration}
            headingLevel={2}
        />
    );
}
